import React from 'react';
import { Box, Container } from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Ticket, TicketStatus } from '@marageti/z4-sdk/lib/tickets';
import {
  EditableTicket, TicketFormData, AssigneeOption, Option,
} from '../../tickets/options';
import { TicketCloseFormData } from '../../../components/modals/ticket-close-modal';
import { TicketReassignFormData } from '../../../components/modals/ticket-reassign-modal';
import TicketList from '../../../components/ticket-list';
import TicketsFilters from '../../tickets/tickets-filters';

type PartnerTicketsViewProps = {
  tickets: Ticket[];
  totalHits: number;
  isLoading: boolean;
  error: ApiError | undefined;
  hasMore: boolean;
  loadMore: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedTypes: Option[];
  onTypeChange: (event: any, values: Option[]) => void;
  assignees: AssigneeOption[];
  selectedAssignee: AssigneeOption;
  onAssigneeChange: (assignee: AssigneeOption | null) => void;
  sortValue: string;
  onSortChange: (sort: string) => void;
  selectedStatusChips: string[];
  onStatusChipChange: (chips: string[]) => void;
  selectedSeverityChips: string[];
  onSeverityChipChange: (chips: string[]) => void;
  isSeverityChipsDisabled: boolean;
  onEditTicket: (ticket: Ticket) => void;
  onReassignTicket: (ticket: Ticket) => void;
  onDeleteTicket: (ticket: Ticket) => void;
  onStatusChange: (ticketId: string, status: TicketStatus) => void;
  isModalOpen: boolean;
  editingTicket?: EditableTicket;
  defaultAssigneeIds?: string[];
  onCloseModal: () => void;
  onSaveTicket: (data: TicketFormData) => Promise<void>;
  isCloseModalOpen: boolean;
  onCloseCloseModal: () => void;
  onSaveClose: (data: TicketCloseFormData) => Promise<void>;
  isReassignModalOpen: boolean;
  reassigningTicketAssigneeIds: string[];
  onCloseReassignModal: () => void;
  onSaveReassign: (data: TicketReassignFormData) => Promise<void>;
  isDeleteModalOpen: boolean;
  onCloseDeleteModal: () => void;
  onConfirmDelete: () => Promise<void>;
};

const PartnerTicketsView = ({
  tickets,
  totalHits,
  isLoading,
  error,
  hasMore,
  loadMore,
  searchValue,
  onSearchChange,
  selectedTypes,
  onTypeChange,
  assignees,
  selectedAssignee,
  onAssigneeChange,
  sortValue,
  onSortChange,
  selectedStatusChips,
  onStatusChipChange,
  selectedSeverityChips,
  onSeverityChipChange,
  isSeverityChipsDisabled,
  onEditTicket,
  onReassignTicket,
  onDeleteTicket,
  onStatusChange,
  isModalOpen,
  editingTicket,
  defaultAssigneeIds,
  onCloseModal,
  onSaveTicket,
  isCloseModalOpen,
  onCloseCloseModal,
  onSaveClose,
  isReassignModalOpen,
  reassigningTicketAssigneeIds,
  onCloseReassignModal,
  onSaveReassign,
  isDeleteModalOpen,
  onCloseDeleteModal,
  onConfirmDelete,
}: PartnerTicketsViewProps) => (
  <Container maxWidth="xl">
    <Box py={3}>
      <TicketsFilters
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        selectedTypes={selectedTypes}
        onTypeChange={onTypeChange}
        assignees={assignees}
        selectedAssignee={selectedAssignee}
        onAssigneeChange={onAssigneeChange}
        sortValue={sortValue}
        onSortChange={onSortChange}
        selectedStatusChips={selectedStatusChips}
        onStatusChipChange={onStatusChipChange}
        selectedSeverityChips={selectedSeverityChips}
        onSeverityChipChange={onSeverityChipChange}
        isSeverityChipsDisabled={isSeverityChipsDisabled}
      />

      <TicketList
        tickets={tickets}
        totalHits={totalHits}
        isLoading={isLoading}
        error={error}
        hasMore={hasMore}
        loadMore={loadMore}
        onEditTicket={onEditTicket}
        onReassignTicket={onReassignTicket}
        onDeleteTicket={onDeleteTicket}
        onStatusChange={onStatusChange}
        isModalOpen={isModalOpen}
        editingTicket={editingTicket}
        assignees={assignees}
        defaultAssigneeIds={defaultAssigneeIds}
        onCloseModal={onCloseModal}
        onSaveTicket={onSaveTicket}
        isCloseModalOpen={isCloseModalOpen}
        onCloseCloseModal={onCloseCloseModal}
        onSaveClose={onSaveClose}
        isReassignModalOpen={isReassignModalOpen}
        reassigningTicketAssigneeIds={reassigningTicketAssigneeIds}
        onCloseReassignModal={onCloseReassignModal}
        onSaveReassign={onSaveReassign}
        isDeleteModalOpen={isDeleteModalOpen}
        onCloseDeleteModal={onCloseDeleteModal}
        onConfirmDelete={onConfirmDelete}
      />
    </Box>
  </Container>
);

export default PartnerTicketsView;
